import { useEffect, useState, useCallback } from 'preact/hooks';
import type { State, Payor, Specialties } from '@services/filter-api/types';

// Global state store for filter selections and validation
const globalFilterSelections = {
  state: null as State | null,
  insurance: null as Payor | null,
  specialty: null as Specialties | null,
  typeOfCare: undefined as string | undefined,
  errors: {
    state: "",
    insurance: "",
  } as {
    state: string;
    insurance: string;
  },
};

// Track which fields have been explicitly synced
const syncedFields = {
  state: false,
  insurance: false,
  specialty: false,
  typeOfCare: false,
  errors: false,
};

// Event types for synchronization
type FilterEventType = 
  | 'state-selected'
  | 'insurance-selected'
  | 'needs-selected'
  | 'type-of-care-changed'
  | 'validation-updated';

interface FilterEvent {
  type: FilterEventType;
  payload?: any;
}

// Event emitter for cross-component communication
class FilterEventEmitter {
  private listeners: Map<FilterEventType, Set<(event: FilterEvent) => void>> = new Map();

  subscribe(eventType: FilterEventType, callback: (event: FilterEvent) => void) {
    if (!this.listeners.has(eventType)) {
      this.listeners.set(eventType, new Set());
    }
    this.listeners.get(eventType)!.add(callback);

    // Return unsubscribe function
    return () => {
      const callbacks = this.listeners.get(eventType);
      if (callbacks) {
        callbacks.delete(callback);
        if (callbacks.size === 0) {
          this.listeners.delete(eventType);
        }
      }
    };
  }

  emit(event: FilterEvent) {
    const callbacks = this.listeners.get(event.type);
    if (callbacks) {
      callbacks.forEach(callback => callback(event));
    }
  }
}

const filterEventEmitter = new FilterEventEmitter();

export const useFilterSync = () => {
  const [selectedItems, setSelectedItems] = useState({
    state: globalFilterSelections.state,
    insurance: globalFilterSelections.insurance,
    specialty: globalFilterSelections.specialty,
  });
  const [typeOfCare, setTypeOfCare] = useState(globalFilterSelections.typeOfCare);
  const [errors, setErrors] = useState(globalFilterSelections.errors);

  // Update global state and emit events
  const updateGlobalState = useCallback((updates: Partial<typeof globalFilterSelections>) => {
    Object.assign(globalFilterSelections, updates);
    
    // Mark fields as synced when they're updated
    if ('state' in updates) syncedFields.state = true;
    if ('insurance' in updates) syncedFields.insurance = true;
    if ('specialty' in updates) syncedFields.specialty = true;
    if ('typeOfCare' in updates) syncedFields.typeOfCare = true;
    if ('errors' in updates) syncedFields.errors = true;
  }, []);

  // Handle state selection
  const handleGetSelectedState = useCallback((item: any) => {
    const newState = item.selected ? item : null;
    setSelectedItems(prev => ({ ...prev, state: newState }));
    updateGlobalState({ state: newState });
    filterEventEmitter.emit({ type: 'state-selected', payload: newState });
    
    // Emit custom DOM event for Filters component (both selection and deselection)
    document.dispatchEvent(new CustomEvent('filter-sync-state-selected', { detail: newState }));
  }, [updateGlobalState]);

  // Handle insurance selection
  const handleGetSelectedInsurance = useCallback((item: any) => {
    const newInsurance = item.selected ? item : null;
    setSelectedItems(prev => ({ ...prev, insurance: newInsurance }));
    updateGlobalState({ insurance: newInsurance });
    filterEventEmitter.emit({ type: 'insurance-selected', payload: newInsurance });
    
    // Emit custom DOM event for Filters component (both selection and deselection)
    document.dispatchEvent(new CustomEvent('filter-sync-insurance-selected', { detail: newInsurance }));
  }, [updateGlobalState]);

  // Handle needs selection
  const handleGetSelectedNeeds = useCallback((items: any[]) => {
    const newSpecialty = items.length ? items : null;
    setSelectedItems(prev => ({ ...prev, specialty: newSpecialty }));
    updateGlobalState({ specialty: newSpecialty });
    filterEventEmitter.emit({ type: 'needs-selected', payload: newSpecialty });
    
    // Emit custom DOM event for Filters component
    if (newSpecialty) {
      document.dispatchEvent(new CustomEvent('filter-sync-needs-selected', { detail: newSpecialty }));
    }
  }, [updateGlobalState]);

  // Handle type of care changes
  const handleTypeOfCareChange = useCallback((newTypeOfCare: string | undefined) => {
    setTypeOfCare(newTypeOfCare);
    updateGlobalState({ typeOfCare: newTypeOfCare });
    filterEventEmitter.emit({ type: 'type-of-care-changed', payload: newTypeOfCare });
    
    // Emit custom DOM event for Filters component
    document.dispatchEvent(new CustomEvent('filter-sync-type-of-care-changed', { detail: newTypeOfCare }));
  }, [updateGlobalState]);

  // Handle validation updates
  const handleValidationUpdate = useCallback((newErrors: { state: string; insurance: string }) => {
    setErrors(newErrors);
    updateGlobalState({ errors: newErrors });
    filterEventEmitter.emit({ type: 'validation-updated', payload: newErrors });
    
    // Emit custom DOM event for Filters component
    document.dispatchEvent(new CustomEvent('filter-sync-validation-updated', { detail: newErrors }));
  }, [updateGlobalState]);

  // Listen for events from other instances
  useEffect(() => {
    const unsubscribeState = filterEventEmitter.subscribe('state-selected', (event) => {
      setSelectedItems(prev => ({ ...prev, state: event.payload }));
    });

    const unsubscribeInsurance = filterEventEmitter.subscribe('insurance-selected', (event) => {
      setSelectedItems(prev => ({ ...prev, insurance: event.payload }));
    });

    const unsubscribeNeeds = filterEventEmitter.subscribe('needs-selected', (event) => {
      setSelectedItems(prev => ({ ...prev, specialty: event.payload }));
    });

    const unsubscribeTypeOfCare = filterEventEmitter.subscribe('type-of-care-changed', (event) => {
      setTypeOfCare(event.payload);
    });

    const unsubscribeValidation = filterEventEmitter.subscribe('validation-updated', (event) => {
      setErrors(event.payload);
    });

    return () => {
      unsubscribeState();
      unsubscribeInsurance();
      unsubscribeNeeds();
      unsubscribeTypeOfCare();
      unsubscribeValidation();
    };
  }, []);

  return {
    selectedItems,
    typeOfCare,
    errors,
    handleGetSelectedState,
    handleGetSelectedInsurance,
    handleGetSelectedNeeds,
    handleTypeOfCareChange,
    handleValidationUpdate,
    syncedFields,
  };
};
